import { CalendarClock, SplitSquareHorizontal, FileCheck2, Repeat } from "lucide-react";
import { Reveal, MaskLines } from "./motion";
import { GALLERIES } from "../../lib/assets";
import { GlassCard, DeviceMock } from "./Glass";

const features = [
  {
    icon: SplitSquareHorizontal,
    title: "Divide un monto total",
    body: "Reparte un importe en varias partes. Ejemplo: de 100 MXF, un pago automatiza 66 y el otro se ajusta con el remanente exacto.",
  },
  {
    icon: CalendarClock,
    title: "Programa cada pago",
    body: "Cada pago tiene su propia fecha, hora e importe. Captura el calendario una vez y Aurexis ejecuta por ti.",
  },
  {
    icon: FileCheck2,
    title: "Certifica tus facturas",
    body: "Adjunta la factura de cada pago y quedará certificada en la Fragoso Smart Chain, con respaldo verificable.",
  },
];

export const Automatizacion = () => {
  return (
    <section
      id="automatizacion"
      data-testid="automatizacion-section"
      className="min-h-screen px-6 pb-24 pt-28 md:px-12"
    >
      <div className="mx-auto max-w-6xl">
        <span className="font-accent text-xs uppercase tracking-[0.2em] text-ink-soft">
          Automatización
        </span>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-normal leading-none tracking-tight text-ink md:text-6xl">
          <MaskLines lines={["Automatiza tus pagos.", "Divide, programa", "y certifica."]} />
        </h2>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-ink-soft md:text-lg">
            Programa transferencias en MXF con la fecha, hora e importe de cada
            pago. Adjunta las facturas para certificarlas en blockchain y divide
            un monto total en las partes que necesites — Aurexis las ejecuta
            solo.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          {/* Devices */}
          <GlassCard testId="automatizacion-device" delay={0.1} className="order-2 p-8 md:p-10 lg:order-1">
            <DeviceMock
              src={GALLERIES.automatizacion[0].src}
              alt={GALLERIES.automatizacion[0].alt}
              className="min-h-[440px]"
              imgClassName="max-h-[540px]"
            />
          </GlassCard>

          {/* Split highlight + features */}
          <div className="order-1 lg:order-2">
            <Reveal delay={0.05}>
              <div className="lg-surface rounded-3xl p-7 md:p-8">
                <div className="flex items-center gap-2">
                  <Repeat className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  <span className="font-accent text-xs uppercase tracking-[0.2em] text-ink-soft">
                    Ejemplo de división
                  </span>
                </div>
                <div className="mt-5 grid grid-cols-3 items-center gap-3">
                  <div className="col-span-3 mb-1 font-body text-sm text-ink-soft">
                    Monto total
                    <span className="ml-2 font-display text-2xl text-ink">100.00 MXF</span>
                  </div>
                  <div className="col-span-2 rounded-2xl bg-white/5 px-5 py-4">
                    <div className="font-accent text-xs uppercase tracking-wider text-ink-soft">Pago 1</div>
                    <div className="mt-1 font-display text-2xl text-ink">66.00 MXF</div>
                  </div>
                  <div className="rounded-2xl bg-white/5 px-5 py-4">
                    <div className="font-accent text-xs uppercase tracking-wider text-ink-soft">Pago 2</div>
                    <div className="mt-1 font-display text-2xl text-ink">34.00 MXF</div>
                    <div className="mt-1 font-accent text-[10px] uppercase tracking-wider text-gold">Remanente</div>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="mt-5 grid grid-cols-1 gap-4">
              {features.map((f, i) => (
                <Reveal key={f.title} delay={0.1 + i * 0.06}>
                  <div
                    data-testid={`auto-feature-${i}`}
                    className="lg-surface flex items-start gap-4 rounded-3xl p-5 transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    <f.icon className="mt-0.5 h-6 w-6 shrink-0 text-gold" strokeWidth={1.5} />
                    <div>
                      <h3 className="font-display text-xl text-ink md:text-2xl">{f.title}</h3>
                      <p className="mt-1 font-body text-sm leading-relaxed text-ink-soft">{f.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
